import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import { ButtonLink } from '@commons/components/Button';
import Icon from '@commons/components/Icon';
import IconButton from '@commons/components/IconButton';
import Link from '@commons/components/Link';
import linkStyles from '@commons/components/Link/index.module.scss';
import LocaleSwitcher from '@commons/components/LocaleSwitcher';
import MobilePane from '@commons/components/PageLayout/Header/MobilePane';
import Text from '@commons/components/Text';
import BaseContainer from '@commons/containers/BaseContainer';
import Flex from '@commons/containers/Flex';
import { isExternalURL } from '@commons/helpers/url';
import type { Alternates } from '@commons/i18n';

import type { MainMenuLink } from '..';
import ContactUsMenu from '../ContactUsMenu';
import styles from './MenuPane.module.scss';

// Type definition of the MenuPane component properties.

type Props = {
  menu: MainMenuLink[];
  visible: boolean;
  alternates: Alternates;
  handleTriggerClick: () => void;
  displayMainCTA?: boolean;
  className?: string;
};

/**
 * Menu pane component.
 */

const MenuPane = ({
  menu = [],
  visible,
  alternates,
  handleTriggerClick,
  displayMainCTA = true,
  className,
}: Props) => {
  const t = useTranslations('menu');

  const renderedTrigger = (
    <IconButton
      type={visible ? 'close' : 'menu'}
      active={visible}
      colors={{ icon: 'black', bg: 'grey-1' }}
      activeColors={{ icon: 'black', bg: 'grey-1' }}
      onClick={handleTriggerClick}
      className="nav_ham_icon"
      aria-label={t('menu')}
    />
  );

  const renderedMenu = menu.map((link) => {
    if (link.isButton) return null;
    return (
      <MenuLink
        {...link}
        key={link.uuid}
        isActive={link.isActive}
        className={clsx(styles.menuLink, link.class)}
        menuVisible={visible}
      />
    );
  });

  const lastMenuItem = menu[menu.length - 1];
  const hasButtonInMainMenu = lastMenuItem?.isButton;
  const renderedMainMenuButton = hasButtonInMainMenu ? (
    <ButtonLink variant="secondary" href={lastMenuItem.url}>
      {lastMenuItem.title}
    </ButtonLink>
  ) : null;

  const classes = clsx(styles.menuPane, className);

  return (
    <MobilePane
      as="nav"
      theme="white"
      trigger={renderedTrigger}
      visible={visible}
      className={classes}
    >
      <Flex justify="space-between" direction="column" className="h-100">
        <Flex gap="sm" direction="column" marginTop="xl" marginBottom="xl">
          {renderedMenu}
        </Flex>
        <Flex gap="xs" align="center" justify="space-between">
          <LocaleSwitcher alternates={alternates} />
          {displayMainCTA ? <ContactUsMenu /> : renderedMainMenuButton}
        </Flex>
      </Flex>
    </MobilePane>
  );
};

/**
 * Menu link component for the mobile menu pane.
 */

const MenuLink = ({
  title,
  url,
  isActive,
  subLinks,
  buttonLabel,
  description,
  className,
  menuVisible,
}: MainMenuLink & { menuVisible: boolean }) => {
  const [subMenuVisible, setSubMenuVisible] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!menuVisible) {
      setSubMenuVisible(false); // Close submenus when the main menu is closed
    }
  }, [menuVisible]);

  const isExternal = isExternalURL(url);
  const renderedLink = subLinks ? (
    <button
      ref={triggerRef}
      type="button"
      aria-expanded={subMenuVisible}
      className={clsx(
        'link',
        linkStyles.link,
        linkStyles.withArrow,
        isExternal && linkStyles.isExternal,
        isActive && ['active', linkStyles.active],
        styles.subMenuTrigger,
        className
      )}
      onClick={() => setSubMenuVisible((prev) => !prev)}
    >
      <Text as="span" level="h3" className={linkStyles.label} marginBottom="none">
        {title}
      </Text>
      <Icon type={isExternal ? 'external' : 'arrowRight'} className={linkStyles.arrow} />
    </button>
  ) : (
    <Link withArrow={true} href={url} level="h3" active={isActive} className={className}>
      {title}
    </Link>
  );

  const renderedDescription = (
    <Text marginBottom="none" className={styles.description}>
      {description}
    </Text>
  );
  const renderedMenuLink = (
    <BaseContainer className={styles.linkGroup}>
      {renderedLink}
      {description ? renderedDescription : null}
    </BaseContainer>
  );

  const backBtn = (
    <button
      type="button"
      className={clsx('link', styles.backBtn)}
      onClick={() => {
        setSubMenuVisible(false);
        triggerRef.current?.focus();
      }}
    >
      <Text
        as="span"
        level="body-sm"
        transform="uppercase"
        marginBottom="none"
        className={styles.backContent}
      >
        <Icon type="chevronLeft" className={styles.backIcon} />
        {title}
      </Text>
    </button>
  );

  if (subLinks) {
    const hasButton = url && url !== '#' && buttonLabel;

    const renderedSubLinks = subLinks.map((subLink) => (
      <MenuLink
        {...subLink}
        key={subLink.uuid}
        isActive={subLink.isActive}
        menuVisible={subMenuVisible}
      />
    ));

    return (
      <MobilePane
        theme="white"
        trigger={renderedMenuLink}
        visible={subMenuVisible}
        className={styles.subMenu}
      >
        {backBtn}
        {renderedDescription}
        <Flex gap="xs" width={12} marginTop="md" direction="column" paddingBottom="md">
          {renderedSubLinks}
        </Flex>
        {hasButton && (
          <ButtonLink variant="primary" href={url}>
            {buttonLabel}
          </ButtonLink>
        )}
      </MobilePane>
    );
  }

  return renderedMenuLink;
};

export default MenuPane;
